/**
 * Host Global - Inspector Inspection Detail Page
 * Inspector can view property details, verify criteria, and submit inspection results
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { inspectorAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function InspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Inspection form data
  const [inspectionData, setInspectionData] = useState({
    amenitiesVerified: [],
    missingAmenities: [],
    additionalNotes: "",
    criteriaVerified: [],
    photos: [],
  });

  // Fetch property details
  useEffect(() => {
    if (id) {
      fetchPropertyDetails();
    } else {
      setMessage({ type: "error", text: "Invalid inspection ID" });
      setLoading(false);
    }
  }, [id]);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      console.log("🔵 Fetching inspection details for ID:", id);
      const response = await inspectorAPI.getInspectionById(id);
      console.log("🔵 API Response:", response);

      // Handle different response structures
      const propertyData = response.property || response.data || response;
      console.log("🔵 Property Data:", propertyData);

      if (!propertyData || (!propertyData._id && !propertyData.id)) {
        throw new Error("Invalid property data received");
      }

      setProperty(propertyData);

      // Initialize criteria verification state
      if (
        propertyData.criteria &&
        Array.isArray(propertyData.criteria) &&
        propertyData.criteria.length > 0
      ) {
        setInspectionData((prev) => ({
          ...prev,
          criteriaVerified: propertyData.criteria.map((c) => {
            const nameValue =
              typeof c.name === "object" && c.name.en
                ? c.name.en
                : typeof c.name === "string"
                ? c.name
                : "";
            const categoryValue =
              typeof c.category === "object" && c.category.en
                ? c.category.en
                : typeof c.category === "string"
                ? c.category
                : "General";
            return {
              name: nameValue,
              category: categoryValue,
              quantifiable: c.quantifiable === true,
              quantity:
                c.quantifiable && c.quantity != null ? c.quantity : c.quantifiable ? 1 : undefined,
              verified: c.verified || false,
              notes: typeof c.notes === "object" && c.notes.en ? c.notes.en : c.notes || "",
            };
          }),
        }));
      } else {
        // Initialize empty criteria if none exist
        setInspectionData((prev) => ({
          ...prev,
          criteriaVerified: [],
        }));
      }
    } catch (error) {
      console.error("❌ Error fetching property:", error);
      console.error("❌ Error details:", {
        message: error.message,
        status: error.status,
        data: error.data,
        stack: error.stack,
      });
      setMessage({
        type: "error",
        text: error.message || "Failed to load property details. Please try again.",
      });
      setProperty(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCriteriaToggle = (criterionName) => {
    setInspectionData((prev) => ({
      ...prev,
      criteriaVerified: prev.criteriaVerified.map((c) => {
        const cName = typeof c.name === "object" && c.name.en ? c.name.en : c.name;
        return cName === criterionName
          ? { ...c, verified: !c.verified, verifiedAt: !c.verified ? new Date() : undefined }
          : c;
      }),
    }));
  };

  const handleCriteriaQuantityChange = (criterionName, quantity) => {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setInspectionData((prev) => ({
      ...prev,
      criteriaVerified: prev.criteriaVerified.map((c) => {
        const cName = typeof c.name === "object" && c.name.en ? c.name.en : c.name;
        return cName === criterionName ? { ...c, quantity: qty } : c;
      }),
    }));
  };

  const handleCriteriaNotesChange = (criterionName, notes) => {
    setInspectionData((prev) => ({
      ...prev,
      criteriaVerified: prev.criteriaVerified.map((c) => {
        const cName = typeof c.name === "object" && c.name.en ? c.name.en : c.name;
        return cName === criterionName ? { ...c, notes } : c;
      }),
    }));
  };

  const handleAmenityToggle = (amenity, isVerified) => {
    setInspectionData((prev) => {
      if (isVerified) {
        const verified = prev.amenitiesVerified.includes(amenity)
          ? prev.amenitiesVerified.filter((a) => a !== amenity)
          : [...prev.amenitiesVerified, amenity];
        const missing = prev.missingAmenities.filter((a) => a !== amenity);
        return { ...prev, amenitiesVerified: verified, missingAmenities: missing };
      } else {
        const missing = prev.missingAmenities.includes(amenity)
          ? prev.missingAmenities.filter((a) => a !== amenity)
          : [...prev.missingAmenities, amenity];
        const verified = prev.amenitiesVerified.filter((a) => a !== amenity);
        return { ...prev, missingAmenities: missing, amenitiesVerified: verified };
      }
    });
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setInspectionData((prev) => ({
      ...prev,
      photos: [...prev.photos, ...files],
    }));
  };

  const removePhoto = (index) => {
    setInspectionData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!property) return;

    // Validate that at least one amenity has been verified or marked as missing
    const hasAmenities =
      property.amenities && Array.isArray(property.amenities) && property.amenities.length > 0;
    const totalCheckedAmenities =
      (inspectionData.amenitiesVerified?.length || 0) +
      (inspectionData.missingAmenities?.length || 0);

    if (hasAmenities && totalCheckedAmenities === 0) {
      setMessage({
        type: "error",
        text: "Please verify at least one amenity (mark it as verified or missing) before submitting the inspection.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const formData = new FormData();
      formData.append("amenitiesVerified", JSON.stringify(inspectionData.amenitiesVerified));
      formData.append("missingAmenities", JSON.stringify(inspectionData.missingAmenities));
      formData.append("additionalNotes", inspectionData.additionalNotes);
      formData.append("criteriaVerified", JSON.stringify(inspectionData.criteriaVerified));

      // Add photos
      inspectionData.photos.forEach((photo) => {
        formData.append("photos", photo);
      });

      const response = await inspectorAPI.submitInspectionResults(id, formData);

      if (response.message) {
        setMessage({ type: "success", text: "Inspection results submitted successfully!" });
        setTimeout(() => {
          navigate("/operations/inspections");
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting inspection:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to submit inspection results",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!property && !loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Property not found or failed to load
          </Alert>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => navigate("/operations/inspections")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to Inspections
          </MDButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Don't render main content if property is not loaded yet
  if (!property) {
    return null;
  }

  // Group criteria by category
  const criteriaByCategory =
    property.criteria && Array.isArray(property.criteria) && property.criteria.length > 0
      ? property.criteria.reduce((acc, criterion) => {
          // Extract category (handle bilingual object)
          const category =
            typeof criterion.category === "object" && criterion.category.en
              ? criterion.category.en
              : typeof criterion.category === "string"
              ? criterion.category
              : "General";
          if (!acc[category]) acc[category] = [];
          acc[category].push(criterion);
          return acc;
        }, {})
      : {};

  const verifiedCount = inspectionData.criteriaVerified.filter((c) => c.verified).length;
  const totalCriteria = inspectionData.criteriaVerified.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Inspection: {property?.title?.en || property?.title || "Loading..."}
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Verify property criteria and submit inspection results
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/operations/inspections")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to Inspections
          </MDButton>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Property Information */}
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  Property Information
                </MDTypography>
                <MDBox mb={1}>
                  <MDTypography variant="caption" color="text.secondary">
                    Address (English)
                  </MDTypography>
                  <MDTypography variant="body2">
                    {property.address?.en || property.address || "N/A"}
                  </MDTypography>
                </MDBox>
                {property.address?.ar && (
                  <MDBox mb={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Address (Arabic)
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ direction: "rtl", textAlign: "right" }}>
                      {property.address.ar}
                    </MDTypography>
                  </MDBox>
                )}
                {property.inspectionLocation && (
                  <MDBox mb={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Inspection Location
                    </MDTypography>
                    <MDTypography variant="body2">
                      {property.inspectionLocation.replace(/Google Maps:.*$/i, "").trim()}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mb={1}>
                  <MDTypography variant="caption" color="text.secondary">
                    Type
                  </MDTypography>
                  <MDTypography variant="body2" textTransform="capitalize">
                    {property.propertyType}
                  </MDTypography>
                </MDBox>
                <MDBox mb={1}>
                  <MDTypography variant="caption" color="text.secondary">
                    Rooms / Bathrooms
                  </MDTypography>
                  <MDTypography variant="body2">
                    {property.rooms} rooms • {property.bathrooms} baths
                  </MDTypography>
                </MDBox>
                {property.inspectionDate && (
                  <MDBox mb={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Inspection Date
                    </MDTypography>
                    <MDTypography variant="body2">
                      {new Date(property.inspectionDate).toLocaleString()}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mb={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    fullWidth
                    onClick={() => {
                      // Extract location text without the Google Maps link
                      let location =
                        property.inspectionLocation ||
                        property.address?.en ||
                        property.address ||
                        `${property.address?.en || property.address || ""}, ${
                          property.city?.en || property.city || ""
                        }`;
                      if (location) {
                        // Remove any Google Maps link text from the location string
                        location = location.replace(/Google Maps:.*$/i, "").trim();
                        // Extract URL if it exists in the string
                        const urlMatch = location.match(/https?:\/\/[^\s]+/);
                        if (urlMatch) {
                          // If there's a URL in the string, use it directly
                          window.open(urlMatch[0], "_blank");
                        } else {
                          // Otherwise, create a search query
                          const encodedLocation = encodeURIComponent(location);
                          window.open(
                            `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`,
                            "_blank"
                          );
                        }
                      }
                    }}
                    startIcon={<Icon>map</Icon>}
                    disabled={
                      !property.inspectionLocation && !property.address?.en && !property.address
                    }
                  >
                    Open in Google Maps
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={3}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                  <Tab
                    label="Criteria Verification"
                    icon={<Icon>checklist</Icon>}
                    iconPosition="start"
                  />
                  <Tab label="Amenities" icon={<Icon>star</Icon>} iconPosition="start" />
                  <Tab label="Notes & Photos" icon={<Icon>note</Icon>} iconPosition="start" />
                </Tabs>

                <MDBox mt={3}>
                  {/* Criteria Verification Tab */}
                  {tabValue === 0 && (
                    <MDBox>
                      {totalCriteria > 0 ? (
                        <>
                          <MDBox mb={3} p={2} bgcolor="info.light" borderRadius={2}>
                            <MDTypography variant="h6" fontWeight="bold" mb={1}>
                              Criteria Verification Progress
                            </MDTypography>
                            <MDTypography variant="body2">
                              Verified: {verifiedCount} / {totalCriteria} criteria
                            </MDTypography>
                          </MDBox>

                          {Object.keys(criteriaByCategory).length > 0 ? (
                            Object.entries(criteriaByCategory).map(([category, criteria]) => {
                              // Extract category name (handle bilingual)
                              const categoryName =
                                typeof category === "object" && category.en
                                  ? category.en
                                  : typeof category === "string"
                                  ? category
                                  : "General";

                              return (
                                <MDBox key={categoryName} mb={3}>
                                  <MDTypography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    mb={2}
                                    color="info.main"
                                  >
                                    {categoryName}
                                  </MDTypography>
                                  <Grid container spacing={2}>
                                    {criteria.map((criterion) => {
                                      // Extract criterion name (handle bilingual)
                                      const criterionName =
                                        typeof criterion.name === "object" && criterion.name.en
                                          ? criterion.name.en
                                          : typeof criterion.name === "string"
                                          ? criterion.name
                                          : "";
                                      const criterionNameKey =
                                        typeof criterion.name === "object" && criterion.name.en
                                          ? criterion.name.en
                                          : criterion.name;

                                      const verifiedData = inspectionData.criteriaVerified.find(
                                        (c) => {
                                          const cName =
                                            typeof c.name === "object" && c.name.en
                                              ? c.name.en
                                              : c.name;
                                          return cName === criterionNameKey;
                                        }
                                      );
                                      const isVerified = verifiedData?.verified || false;

                                      return (
                                        <Grid item xs={12} sm={6} key={criterionNameKey}>
                                          <Card
                                            sx={{
                                              p: 2,
                                              border: isVerified ? "2px solid" : "1px solid",
                                              borderColor: isVerified ? "success.main" : "grey.300",
                                              bgcolor: isVerified
                                                ? "rgba(76, 175, 80, 0.05)"
                                                : "transparent",
                                              transition: "all 0.3s ease",
                                              "&:hover": {
                                                boxShadow: 2,
                                                transform: "translateY(-2px)",
                                              },
                                            }}
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={isVerified}
                                                  onChange={() =>
                                                    handleCriteriaToggle(criterionNameKey)
                                                  }
                                                  color="success"
                                                />
                                              }
                                              label={
                                                <MDTypography variant="body2" fontWeight="medium">
                                                  {criterionName}
                                                  {criterion.quantifiable && (
                                                    <MDTypography
                                                      component="span"
                                                      variant="caption"
                                                      color="info.main"
                                                      sx={{ ml: 0.5 }}
                                                    >
                                                      (quantifiable)
                                                    </MDTypography>
                                                  )}
                                                </MDTypography>
                                              }
                                            />
                                            {criterion.quantifiable && (
                                              <MDBox
                                                mt={1}
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                              >
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Quantity:
                                                </MDTypography>
                                                <TextField
                                                  type="number"
                                                  size="small"
                                                  value={
                                                    verifiedData?.quantity ??
                                                    criterion.quantity ??
                                                    1
                                                  }
                                                  onChange={(e) =>
                                                    handleCriteriaQuantityChange(
                                                      criterionNameKey,
                                                      e.target.value
                                                    )
                                                  }
                                                  inputProps={{ min: 1 }}
                                                  sx={{ width: 90 }}
                                                  disabled={!isVerified}
                                                />
                                              </MDBox>
                                            )}
                                            <MDBox mt={1.5}>
                                              <MDTypography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight="medium"
                                                display="block"
                                                mb={0.5}
                                              >
                                                Inspector Comments:
                                              </MDTypography>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                placeholder={
                                                  isVerified
                                                    ? "Add your comments about this criteria..."
                                                    : "Check the box first to add comments"
                                                }
                                                value={verifiedData?.notes || ""}
                                                onChange={(e) =>
                                                  handleCriteriaNotesChange(
                                                    criterionNameKey,
                                                    e.target.value
                                                  )
                                                }
                                                disabled={!isVerified}
                                                multiline
                                                rows={3}
                                                sx={{
                                                  "& .MuiOutlinedInput-root": {
                                                    backgroundColor: isVerified
                                                      ? "white"
                                                      : "grey.50",
                                                  },
                                                }}
                                              />
                                            </MDBox>
                                          </Card>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </MDBox>
                              );
                            })
                          ) : (
                            <MDBox textAlign="center" py={4}>
                              <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}>
                                checklist
                              </Icon>
                              <MDTypography variant="h6" color="text.secondary" mb={1}>
                                No Criteria Available
                              </MDTypography>
                              <MDTypography variant="body2" color="text.secondary">
                                This property doesn't have any criteria to verify.
                              </MDTypography>
                            </MDBox>
                          )}
                        </>
                      ) : (
                        <MDBox textAlign="center" py={4}>
                          <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}>
                            checklist
                          </Icon>
                          <MDTypography variant="h6" color="text.secondary" mb={1}>
                            No Criteria Available
                          </MDTypography>
                          <MDTypography variant="body2" color="text.secondary">
                            This property doesn't have any criteria to verify. You can still verify
                            amenities and add notes.
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  )}

                  {/* Amenities Tab */}
                  {tabValue === 1 && (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Verify Amenities
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <MDBox p={2} bgcolor="success.light" borderRadius={2} mb={2}>
                            <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                              Verified Amenities
                            </MDTypography>
                            {property.amenities?.map((amenity) => {
                              const amenityLabel =
                                typeof amenity === "object" && amenity.en
                                  ? amenity.en
                                  : typeof amenity === "string"
                                  ? amenity
                                  : "";
                              const amenityKey =
                                typeof amenity === "object" && amenity.en ? amenity.en : amenity;
                              return (
                                <Chip
                                  key={amenityKey}
                                  label={amenityLabel}
                                  onClick={() => handleAmenityToggle(amenityKey, true)}
                                  color={
                                    inspectionData.amenitiesVerified.includes(amenityKey)
                                      ? "success"
                                      : "default"
                                  }
                                  variant={
                                    inspectionData.amenitiesVerified.includes(amenityKey)
                                      ? "filled"
                                      : "outlined"
                                  }
                                  sx={{ m: 0.5, cursor: "pointer" }}
                                  icon={
                                    inspectionData.amenitiesVerified.includes(amenityKey) ? (
                                      <Icon>check</Icon>
                                    ) : (
                                      <Icon>add</Icon>
                                    )
                                  }
                                />
                              );
                            })}
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox p={2} bgcolor="error.light" borderRadius={2} mb={2}>
                            <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                              Missing Amenities
                            </MDTypography>
                            {property.amenities?.map((amenity) => {
                              const amenityLabel =
                                typeof amenity === "object" && amenity.en
                                  ? amenity.en
                                  : typeof amenity === "string"
                                  ? amenity
                                  : "";
                              const amenityKey =
                                typeof amenity === "object" && amenity.en ? amenity.en : amenity;
                              return (
                                <Chip
                                  key={amenityKey}
                                  label={amenityLabel}
                                  onClick={() => handleAmenityToggle(amenityKey, false)}
                                  color={
                                    inspectionData.missingAmenities.includes(amenityKey)
                                      ? "error"
                                      : "default"
                                  }
                                  variant={
                                    inspectionData.missingAmenities.includes(amenityKey)
                                      ? "filled"
                                      : "outlined"
                                  }
                                  sx={{ m: 0.5, cursor: "pointer" }}
                                  icon={
                                    inspectionData.missingAmenities.includes(amenityKey) ? (
                                      <Icon>close</Icon>
                                    ) : (
                                      <Icon>add</Icon>
                                    )
                                  }
                                />
                              );
                            })}
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  )}

                  {/* Notes & Photos Tab */}
                  {tabValue === 2 && (
                    <MDBox>
                      <MDBox mb={3}>
                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                          Additional Notes
                        </MDTypography>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          placeholder="Add any additional notes about the inspection..."
                          value={inspectionData.additionalNotes}
                          onChange={(e) =>
                            setInspectionData((prev) => ({
                              ...prev,
                              additionalNotes: e.target.value,
                            }))
                          }
                        />
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                          Inspection Photos
                        </MDTypography>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoChange}
                          style={{ display: "none" }}
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload">
                          <MDButton
                            component="span"
                            variant="outlined"
                            color="dark"
                            startIcon={<Icon>add_photo_alternate</Icon>}
                          >
                            Add Photos
                          </MDButton>
                        </label>
                        {inspectionData.photos.length > 0 && (
                          <Grid container spacing={2} mt={2}>
                            {inspectionData.photos.map((photo, index) => (
                              <Grid item xs={6} sm={4} md={3} key={index}>
                                <Box
                                  component="img"
                                  src={URL.createObjectURL(photo)}
                                  alt={`Inspection photo ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 150,
                                    objectFit: "cover",
                                    borderRadius: 2,
                                    cursor: "pointer",
                                  }}
                                />
                                <MDButton
                                  size="small"
                                  color="error"
                                  onClick={() => removePhoto(index)}
                                  sx={{ mt: 1 }}
                                  fullWidth
                                >
                                  Remove
                                </MDButton>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Submit Button */}
        <MDBox
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDBox flex={1}>
            <MDTypography variant="body2" color="text.secondary">
              <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: "1rem" }}>info</Icon>
              Make sure to verify all criteria and add comments before submitting. Your feedback
              will be sent to Admin and Super Admin for review.
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="success"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || totalCriteria === 0}
            startIcon={submitting ? <CircularProgress size={20} /> : <Icon>send</Icon>}
            sx={{ minWidth: 200 }}
          >
            {submitting ? "Submitting..." : "Send Feedback to Admin"}
          </MDButton>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InspectionDetail;
